export const OAUTH_POPUP_FEATURES = "popup=yes,width=520,height=680";

const OAUTH_POPUP_POLL_MS = 400;

/** Generous — a provider login can involve 2FA and an asset picker. */
const OAUTH_POPUP_TIMEOUT_MS = 5 * 60 * 1000;

/** `timedOut` also covers an abort, which is how an unmount ends the watch. */
export type PopupWaitOutcome = "closed" | "timedOut";

/** Whether the flow ended up in the popup or fell back to a full-page redirect. */
export type AuthorizationNavigation = "popup" | "redirect";

/**
 * Opens the popup blank. Must be called in the click's own task, *before* any
 * await, or Safari treats the window as unsolicited and blocks it.
 */
export function openOAuthPopup(name: string): Window | null {
  return window.open("", name, OAUTH_POPUP_FEATURES);
}

/**
 * Only `https:` is a legitimate authorization endpoint, and anything else is
 * refused before it can be navigated to.
 *
 * This matters more than the usual "validate your inputs": the popup is opened
 * as `about:blank`, which makes it same-origin with gail-ui and leaves it a live
 * `opener`. A `javascript:` URL assigned to its `location` would therefore
 * execute in *our* origin with access back to this window — and the blocked-popup
 * fallback assigns the same value to `window.location.href`, in our origin
 * directly. `http:` is refused too: an authorization URL carries the state
 * parameter, and sending it in the clear is a downgrade we never need to accept.
 */
export function assertHttpsAuthorizationUrl(url: string): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error(`Authorization URL is not a valid absolute URL: "${url}"`);
  }

  if (parsed.protocol !== "https:") {
    throw new Error(
      `Refusing to open an authorization URL over "${parsed.protocol}" — https is required`
    );
  }
}

/**
 * Sends the flow to the provider. Throws — into the caller's existing connect
 * failure path — if the URL is not one we are willing to navigate to.
 *
 * A blocked popup falls back to navigating this tab, which is the only way left
 * to finish the grant.
 */
export function navigateToAuthorizationUrl(
  popup: Window | null,
  url: string
): AuthorizationNavigation {
  assertHttpsAuthorizationUrl(url);

  if (!popup) {
    window.location.href = url;
    return "redirect";
  }

  popup.location.href = url;
  return "popup";
}

/** Resolves when the popup closes, the wait times out, or `signal` aborts. */
export function waitForPopupClose(
  popup: Window,
  signal?: AbortSignal
): Promise<PopupWaitOutcome> {
  return new Promise((resolve) => {
    if (popup.closed) {
      resolve("closed");
      return;
    }
    if (signal?.aborted) {
      resolve("timedOut");
      return;
    }

    let intervalId = 0;
    let timeoutId = 0;
    const finish = (outcome: PopupWaitOutcome) => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      resolve(outcome);
    };
    const onAbort = () => finish("timedOut");

    intervalId = window.setInterval(() => {
      if (popup.closed) finish("closed");
    }, OAUTH_POPUP_POLL_MS);
    timeoutId = window.setTimeout(() => finish("timedOut"), OAUTH_POPUP_TIMEOUT_MS);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
